import { logger } from '../../lib/logger';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowRight, ExternalLink, Lock, X, FileText, CheckCircle2, Calendar as CalIcon, Sparkles, Users } from 'lucide-react';
import { BRAND, VSL_VIDEOS, SOCIALS } from '../../lib/brand';
import { toast } from 'sonner';

const VSL_CONFIG = {
  initial: {
    key: 'vsl_initial',
    title: 'Green Paradise · La nostra visione',
    subtitle: 'Tre resort premium: proprietà con piano pagamenti rateale.',
    poster: BRAND.heroLombok,
    durationS: 420,
    branchHint: 'pre_branch',
  },
  // ★ 2026-08-13 (Fase 51) — Variante "equity" MANTENUTA nel routing per non rompere link
  //   esistenti, ma reindirizza contenuto alla pagina Real Estate (compliance CONSOB).
  equity: {
    key: 'vsl_real_estate',
    title: 'VSL Verification Pass · Real Estate',
    subtitle: 'Compra un appartamento in un resort operativo con piano pagamenti rateale.',
    poster: BRAND.heroSecondary,
    durationS: 420,
    branchHint: 'real_estate',
  },
  'real-estate': {
    key: 'vsl_real_estate',
    title: 'VSL Verification Pass · Real Estate',
    subtitle: 'Real Estate non si valuta su una brochure: scopri il Verification Pass.',
    poster: BRAND.heroSecondary,
    durationS: 420,
    branchHint: 'real_estate',
  },
};

const SKIP_THRESHOLD = 0.75;

function fmtTime(s) {
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function VslPage() {
  const { variant } = useParams();
  const video = VSL_VIDEOS[variant] || VSL_VIDEOS.initial;
  const cfg = VSL_CONFIG[variant] || VSL_CONFIG.initial;

  // LIVE variant (Equity) → render Calendly booking instead of VSL
  if (video?.live) {
    return <EquityLiveBooking video={video} cfg={cfg} />;
  }
  return <VslMandatoryPage cfg={cfg} video={video} />;
}

function VslMandatoryPage({ cfg, video }) {
  const [params] = useSearchParams();
  const leadId = params.get('lead_id');
  const nav = useNavigate();

  const [modalOpen, setModalOpen] = useState(true);
  const [played, setPlayed] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [reportedHalf, setReportedHalf] = useState(false);
  const tickRef = useRef(null);

  // After-VSL booking modal
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingDone, setBookingDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', preferred_date: '', preferred_slot: 'Mattina (9-12)',
    consent_privacy: true,
  });

  const total = cfg.durationS;
  const remaining = Math.max(0, total - elapsed);
  const progress = Math.min(100, (elapsed / total) * 100);
  const canClose = elapsed / total >= SKIP_THRESHOLD;

  useEffect(() => {
    if (!played || completed) return;
    tickRef.current = setInterval(() => {
      setElapsed((e) => {
        const ne = e + 1;
        if (ne >= total) {
          setCompleted(true);
          clearInterval(tickRef.current);
          if (leadId) {
            api.post('/public/vsl-event', { lead_id: leadId, vsl_key: cfg.key, progress_percent: 100, watched_seconds: total, completed: true }).catch(() => {});
          }
          return total;
        }
        return ne;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [played, completed, total, leadId, cfg.key]);

  useEffect(() => {
    if (!leadId || reportedHalf || elapsed / total < 0.5) return;
    setReportedHalf(true);
    api.post('/public/vsl-event', { lead_id: leadId, vsl_key: cfg.key, progress_percent: 50, watched_seconds: elapsed, completed: false }).catch(() => {});
  }, [elapsed, total, leadId, reportedHalf, cfg.key]);

  // When completed or skipped at 75%, auto-open booking modal
  const closeVslAndOpenBooking = () => {
    if (leadId && elapsed / total < 1) {
      api.post('/public/vsl-event', { lead_id: leadId, vsl_key: cfg.key, progress_percent: Math.floor(progress), watched_seconds: elapsed, completed: false }).catch(() => {});
    }
    setModalOpen(false);
    setTimeout(() => setBookingOpen(true), 300);
  };

  const handleSkip = () => {
    if (!canClose) {
      toast.info(`Devi guardare almeno il 75% (mancano ${fmtTime(Math.max(0, total * SKIP_THRESHOLD - elapsed))}).`);
      return;
    }
    closeVslAndOpenBooking();
  };

  useEffect(() => {
    if (completed) {
      // when completed, after a small delay, open booking modal
      const t = setTimeout(() => closeVslAndOpenBooking(), 1200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed]);

  const submitBooking = async () => {
    if (!form.name || !form.email) { toast.error('Nome ed email obbligatori'); return; }
    if (!form.consent_privacy) { toast.error('Devi accettare la privacy'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/public/vsl-booking', {
        ...form,
        branch: cfg.branchHint,
        lead_id: leadId,
        vsl_key: cfg.key,
      });
      setBookingDone(r.data);
      toast.success('Prenotazione confermata! Ti inviamo i materiali via email.');
    } catch (e) {
      toast.error('Errore. Riprova.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-1)]">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
          <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="h-6 w-auto" />
          <span>← Torna alla home</span>
        </Link>
        <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mt-6">Video</div>
        <h1 className="font-serif text-3xl md:text-5xl text-[hsl(var(--primary))] mt-1" data-testid="vsl-title">{cfg.title}</h1>
        <p className="text-sm text-muted-foreground mt-2">{cfg.subtitle}</p>

        <Card className="mt-6 border-border overflow-hidden" data-testid="vsl-page-card">
          <div className="relative aspect-video bg-black">
            {!modalOpen ? (
              <iframe
                title={video.title}
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0&modestbranding=1&autoplay=1&mute=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="vsl-iframe-page"
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center" style={{ backgroundImage: `url(${cfg.poster})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative text-white text-center px-4">
                  <Lock className="w-8 h-8 mx-auto opacity-80" />
                  <div className="font-serif text-2xl mt-2">Il video si apre in modalità immersiva sopra</div>
                </div>
              </div>
            )}
          </div>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-muted-foreground">
              {modalOpen ? 'Guarda il video qui sopra. Al termine sblocchi materiali + call.' : 'Hai già guardato la VSL.'}
            </div>
            <div className="flex gap-2">
              {!modalOpen && (
                <>
                  <Button variant="secondary" size="sm" onClick={() => { setModalOpen(true); setPlayed(false); }} data-testid="vsl-replay-button">Riavvia video</Button>
                  <Button size="sm" onClick={() => setBookingOpen(true)} data-testid="vsl-open-booking">Prenota call &amp; materiali <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <a href={SOCIALS.yt_group} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground">
            <ExternalLink className="w-3 h-3" /> Canale YouTube HCF Group
          </a>
        </div>
      </div>

      {/* MANDATORY VSL MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/95" data-testid="vsl-modal">
          <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-8 py-4 flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="h-9 w-auto bg-white/10 rounded-md p-1" />
              <div className="hidden md:block">
                <div className="font-serif text-lg leading-tight">{cfg.title}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase opacity-70">{video.title}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right font-mono text-sm tabular-nums" data-testid="vsl-countdown">
                <div className="text-[10px] tracking-widest uppercase opacity-60">{completed ? 'Completato' : (played ? 'Tempo restante' : 'Durata')}</div>
                <div className="text-2xl">{played ? fmtTime(remaining) : fmtTime(total)}</div>
              </div>
              <button
                onClick={handleSkip}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs ${canClose ? 'bg-white text-black hover:bg-white/90' : 'bg-white/10 text-white/60 cursor-not-allowed'}`}
                data-testid="vsl-modal-skip"
                title={canClose ? 'Chiudi (75% raggiunto)' : `Salta disponibile al 75% (${Math.floor(progress)}%)`}
              >
                {canClose ? <X className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {canClose ? 'Continua' : `Salta al 75%`}
              </button>
            </div>
          </div>

          <div className="w-full h-full grid place-items-center px-4 md:px-12">
            <div className="w-full max-w-5xl aspect-video relative rounded-xl overflow-hidden shadow-2xl">
              {!played ? (
                <button
                  onClick={() => setPlayed(true)}
                  className="absolute inset-0 grid place-items-center group"
                  style={{ backgroundImage: `url(${cfg.poster})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                  data-testid="vsl-modal-play"
                >
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                  <div className="relative w-24 h-24 rounded-full bg-white grid place-items-center shadow-2xl group-hover:scale-105 transition-transform">
                    <div className="w-0 h-0 border-l-[24px] border-l-[hsl(var(--primary))] border-y-[16px] border-y-transparent ml-2" />
                  </div>
                  <div className="absolute bottom-6 left-0 right-0 text-center text-white text-sm tracking-widest uppercase opacity-80">Premi Play · {fmtTime(total)} totali</div>
                </button>
              ) : (
                <iframe
                  title={video.title}
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&mute=0`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="vsl-modal-iframe"
                />
              )}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-12 py-4 text-white">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center justify-between text-[11px] tracking-widest uppercase opacity-70 mb-1.5">
                <span>Progresso</span>
                <span>{Math.floor(progress)}% {!canClose && `— sblocco al 75%`}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/15 overflow-hidden" data-testid="vsl-modal-progress">
                <div className="h-full bg-gradient-to-r from-[hsl(var(--primary))] to-[var(--gp-gold-soft)]" style={{ width: `${progress}%`, transition: 'width 0.5s linear' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POST-VSL BOOKING + MATERIALS MODAL */}
      {bookingOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => !bookingDone && setBookingOpen(false)} data-testid="post-vsl-modal">
          <div className="max-w-lg w-full bg-card rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {!bookingDone ? (
              <>
                <div className="p-5 border-b border-border bg-[var(--surface-2)]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CalIcon className="w-4 h-4" />
                      <div className="font-serif text-lg">Sblocca tutto in 30 secondi</div>
                    </div>
                    <button onClick={() => setBookingOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="post-vsl-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Lascia un contatto + scegli quando ti chiamiamo. Riceverai subito via email <b>brochure</b> e <b>project financing</b> di tutti e 3 i resort (Lombok, Spille, Chumphon).
                  </p>
                </div>
                <div className="p-5 space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1.5"><Label>Nome*</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="post-vsl-name" /></div>
                    <div className="space-y-1.5"><Label>Email*</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="post-vsl-email" /></div>
                    <div className="space-y-1.5"><Label>Telefono / Telegram</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39…" data-testid="post-vsl-phone" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Data preferita</Label><Input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="post-vsl-date" /></div>
                      <div className="space-y-1.5">
                        <Label>Fascia</Label>
                        <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.preferred_slot} onChange={(e) => setForm({ ...form, preferred_slot: e.target.value })} data-testid="post-vsl-slot">
                          <option>Mattina (9-12)</option>
                          <option>Pranzo (12-14)</option>
                          <option>Pomeriggio (14-17)</option>
                          <option>Sera (17-20)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-[11px] text-muted-foreground">
                    <input type="checkbox" className="mt-0.5" checked={form.consent_privacy} onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })} data-testid="post-vsl-consent" />
                    <span>Acconsento al trattamento dei dati ai sensi del GDPR.</span>
                  </label>
                  <Button className="w-full" onClick={submitBooking} disabled={submitting} data-testid="post-vsl-submit">
                    {submitting ? 'Invio…' : 'Conferma & ricevi i materiali'}
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">Ti contatteremo entro 24h. Email di conferma immediata con tutti i link.</p>
                </div>
              </>
            ) : (
              <div className="p-6 space-y-4" data-testid="post-vsl-success">
                <div className="text-center">
                  <CheckCircle2 className="w-12 h-12 text-[hsl(var(--primary))] mx-auto" />
                  <div className="font-serif text-2xl mt-2 text-[hsl(var(--primary))]">Tutto pronto!</div>
                  <p className="text-sm text-muted-foreground mt-1">Ti abbiamo inviato tutto via email. Ecco subito qui i link:</p>
                </div>
                <div className="space-y-2">
                  {bookingDone.materials.map((m) => (
                    <div key={m.slug} className="rounded-lg border border-border p-3" data-testid={`materials-${m.slug}`}>
                      <div className="font-medium text-sm mb-1.5">Green Paradise {m.resort}</div>
                      <div className="flex gap-2">
                        <a href={m.brochure_url} target="_blank" rel="noreferrer" className="flex-1"><Button variant="secondary" size="sm" className="w-full justify-start"><FileText className="w-3.5 h-3.5 mr-1" />Brochure</Button></a>
                        <a href={m.financing_url} target="_blank" rel="noreferrer" className="flex-1"><Button variant="secondary" size="sm" className="w-full justify-start"><FileText className="w-3.5 h-3.5 mr-1" />Project Financing</Button></a>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="default" className="w-full" onClick={() => nav('/')} data-testid="post-vsl-finish">Torna alla home <ArrowRight className="w-4 h-4 ml-1" /></Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// EquityLiveBooking — Calendly-based alternative to VSL for Equity
// ============================================================
function EquityLiveBooking({ video, cfg }) {
  const [params] = useSearchParams();
  const leadId = params.get('lead_id');
  const [calendlyReady, setCalendlyReady] = useState(false);

  // Detect placeholder Calendly (404 page) → show fallback callback form instead
  const isPlaceholderCalendly = !video.calendlyUrl || video.calendlyUrl.includes('hcf-greenparadise/equity-consultation');

  useEffect(() => {
    // Inject Calendly widget script once
    if (document.getElementById('calendly-widget-script')) {
      setCalendlyReady(true);
      return;
    }
    const s = document.createElement('script');
    s.id = 'calendly-widget-script';
    s.src = 'https://assets.calendly.com/assets/external/widget.js';
    s.async = true;
    s.onload = () => setCalendlyReady(true);
    document.body.appendChild(s);
    // Track engagement event
    if (leadId) {
      api.post('/public/vsl-event', { lead_id: leadId, vsl_key: 'vsl_equity', progress_percent: 0, watched_seconds: 0, completed: false }).catch(() => {});
    }
  }, [leadId]);

  return (
    <div className="min-h-screen bg-[var(--surface-1)]" data-testid="vsl-equity-live">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
          <img src={BRAND.greenParadiseLogoSmall} alt="Green Paradise" className="h-6 w-auto" />
          <span>← Torna alla home</span>
        </Link>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Left: value prop */}
          <div className="md:col-span-2 space-y-5">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[var(--gp-gold)]">Real Estate · Live Consultation</div>
              <h1 className="font-serif text-3xl md:text-4xl text-[hsl(var(--primary))] mt-2 leading-tight" data-testid="equity-live-title">
                Parla live con un agente.<br />Nessun video. Solo risposte.
              </h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Comprare un appartamento in un resort operativo è un&apos;operazione strutturata.
                Merita risposte strutturate. <b>Prenota direttamente</b> una call 1:1 con
                Richard Demichele o un membro del Team HCF Property e ottieni:
              </p>
            </div>
            <ul className="space-y-3">
              {[
                { icon: Users, title: 'Conversazione 1:1 con founder/team', desc: 'Nessun intermediario, parli direttamente con chi ha strutturato il deal.' },
                { icon: Sparkles, title: 'Proiezione personalizzata', desc: 'Prudente, media, aggressiva — calibrata sul tuo capitale e orizzonte.' },
                { icon: FileText, title: 'Materiali on-demand', desc: 'Brochure, project financing e KPI shared live durante la call.' },
                { icon: CheckCircle2, title: 'Zero pressione commerciale', desc: 'Se non ti torna, non ti torna. Ti diciamo anche quando non è per te.' },
              ].map((b) => (
                <li key={b.title} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[hsl(var(--accent))] grid place-items-center shrink-0 border border-[hsl(var(--primary))]/20">
                    <b.icon className="w-4 h-4 text-[hsl(var(--primary))]" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{b.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{b.desc}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="rounded-xl border border-[var(--gp-gold)]/30 bg-[var(--gp-gold)]/5 p-4 text-xs text-muted-foreground">
              <div className="font-medium text-foreground mb-1">Chi chiamerai</div>
              <div>Richard Demichele (Founder & CEO) o un membro del Team HCF Property. Niente intermediari, parli con chi ha strutturato il deal.</div>
            </div>
          </div>

          {/* Right: Calendly inline OR fallback lead form */}
          <div className="md:col-span-3">
            {isPlaceholderCalendly ? (
              <EquityCallbackForm />
            ) : (
              <>
                <Card className="border-border overflow-hidden">
                  <div className="px-4 py-3 border-b border-border bg-[var(--surface-2)] flex items-center gap-2">
                    <CalIcon className="w-4 h-4 text-[hsl(var(--primary))]" />
                    <div className="font-medium text-sm">Scegli il tuo slot</div>
                  </div>
                  <div
                    className="calendly-inline-widget"
                    data-url={`${video.calendlyUrl}?hide_gdpr_banner=1&primary_color=1e4a3a`}
                    style={{ minWidth: '320px', height: '720px' }}
                    data-testid="calendly-embed"
                  />
                  {!calendlyReady && (
                    <div className="p-10 text-center text-sm text-muted-foreground">
                      Caricamento calendario…
                    </div>
                  )}
                </Card>
                <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Prenotazione sicura via Calendly</span>
                  <a href={video.calendlyUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground">
                    Apri in nuova finestra <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EquityCallbackForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', resort: 'all', capital: '', when: 'asap', consent_privacy: true });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email) { toast.error('Nome ed email obbligatori'); return; }
    if (!form.consent_privacy) { toast.error('Devi accettare la privacy'); return; }
    setSubmitting(true);
    try {
      await api.post('/public/materials-request', {
        name: form.name,
        email: form.email,
        phone: form.phone,
        resort_slugs: form.resort === 'all' ? ['lombok', 'la-mar-residence', 'chumphon', 'narea'] : [form.resort],
        interest_path: 'real_estate',
        consent_privacy: true,
        utm: { source: 'vsl_realestate_callback', capital: form.capital, when: form.when },
      });
      setDone(true);
      toast.success('Richiesta ricevuta. Ti richiamiamo entro 24h.');
    } catch (err) {
      logger.debug('VslPage materials submit failed', err);
      toast.error('Errore. Riprova.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <Card className="border-border overflow-hidden" data-testid="equity-callback-success">
        <div className="p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))]/10 grid place-items-center mx-auto">
            <CheckCircle2 className="w-6 h-6 text-[hsl(var(--primary))]" />
          </div>
          <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-4">Richiesta ricevuta</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Il team Equity Desk ti richiamerà entro 24 ore al numero che hai indicato. Riceverai anche un'email di conferma con i materiali ufficiali.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-border overflow-hidden" data-testid="equity-callback-form">
      <div className="px-4 py-3 border-b border-border bg-[var(--surface-2)] flex items-center gap-2">
        <CalIcon className="w-4 h-4 text-[hsl(var(--primary))]" />
        <div className="font-medium text-sm">Richiedi una call 1:1</div>
      </div>
      <div className="p-5 space-y-3">
        <div className="text-xs text-muted-foreground">Lasciaci i tuoi dati e ti richiamiamo entro <b>24 ore</b> con uno slot a tua scelta.</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Nome*</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" data-testid="equity-cb-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email*</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="mario@email.com" data-testid="equity-cb-email" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Telefono / Telegram*</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 1234567" data-testid="equity-cb-phone" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">Resort di interesse</Label>
            <select value={form.resort} onChange={(e) => setForm({ ...form, resort: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="equity-cb-resort">
              <option value="all">Tutti i resort</option>
              <option value="lombok">Lombok · Indonesia</option>
              <option value="spille">Spille · Albania</option>
              <option value="chumphon">Chumphon · Thailandia</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Capitale orientativo</Label>
            <select value={form.capital} onChange={(e) => setForm({ ...form, capital: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="equity-cb-capital">
              <option value="">Preferisco non dirlo</option>
              <option value="40-70k">€40k – €70k (1 quota)</option>
              <option value="70-150k">€70k – €150k (2 quote)</option>
              <option value="150-300k">€150k – €300k</option>
              <option value="300k+">€300k+</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Quando vorresti chiamare?</Label>
            <select value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="equity-cb-when">
              <option value="asap">Il prima possibile</option>
              <option value="this_week">Questa settimana</option>
              <option value="next_week">La prossima settimana</option>
              <option value="evening">Solo serale</option>
            </select>
          </div>
        </div>
        <label className="flex items-start gap-2 text-[11px] text-muted-foreground pt-2">
          <input type="checkbox" className="mt-0.5" checked={form.consent_privacy} onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })} data-testid="equity-cb-consent" />
          <span>Acconsento al trattamento dei dati ai sensi del GDPR. <Link to="/privacy" className="underline">Privacy</Link>.</span>
        </label>
        <Button className="w-full" size="lg" onClick={submit} disabled={submitting} data-testid="equity-cb-submit">
          {submitting ? 'Invio…' : 'Richiedi richiamata'}
        </Button>
      </div>
    </Card>
  );
}
